import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Toast from '../../components/Common/Toast';
import ConfirmModal from '../../components/Common/ConfirmModal';
import PointOfSaleForm from '../../components/Configuration/PointOfSaleForm';
import { getPointOfSales, deletePointOfSale } from '../../services/pointOfSalesService';

const PointOfSales = () => {
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingPointOfSale, setEditingPointOfSale] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [pointOfSaleToDelete, setPointOfSaleToDelete] = useState(null);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const hasLoaded = useRef(false);
  const isLoadingRef = useRef(false);
  const [allPointOfSales, setAllPointOfSales] = useState([]);

  // Cargar puntos de venta desde el backend (solo una vez)
  const loadPointOfSales = async () => {
    // Prevenir llamadas duplicadas simultáneas
    if (isLoadingRef.current) {
      console.log('⏸️ [PointOfSales] Ya hay una carga en progreso, ignorando llamada duplicada');
      return;
    }

    isLoadingRef.current = true;
    setLoading(true);
    try {
      console.log('🔄 [PointOfSales] Iniciando carga de puntos de venta...');
      
      const response = await getPointOfSales();
      
      console.log('📥 [PointOfSales] Respuesta recibida:');
      console.log('📦 [PointOfSales] response:', response);
      console.log('✅ [PointOfSales] response.success:', response.success);
      console.log('📋 [PointOfSales] response.data:', response.data);
      
      if (response.success) {
        const pointOfSalesData = response.data || [];
        console.log('✅ [PointOfSales] Puntos de venta recibidos:', pointOfSalesData.length);
        if (pointOfSalesData.length > 0) {
          console.log('📋 [PointOfSales] Primeros 3 puntos de venta:', pointOfSalesData.slice(0, 3));
        }
        setAllPointOfSales(pointOfSalesData);
      } else {
        console.warn('⚠️ [PointOfSales] Respuesta sin éxito:', response);
        setToast({
          isOpen: true,
          message: 'Error al cargar los puntos de venta',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('❌ [PointOfSales] Error al cargar puntos de venta:', error);
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al cargar los puntos de venta',
        type: 'error'
      });
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
      console.log('🏁 [PointOfSales] Proceso de carga finalizado');
    }
  };

  useEffect(() => {
    // Evitar doble carga en React StrictMode - Carga inicial única
    if (!hasLoaded.current) {
      hasLoaded.current = true;
      console.log('🚀 [PointOfSales] Primera carga - ejecutando loadPointOfSales()');
      loadPointOfSales();
    } else {
      console.log('⏭️ [PointOfSales] Ya se cargó anteriormente, omitiendo carga duplicada');
    }
  }, []);

  // Filtrar puntos de venta localmente en el frontend
  const filteredPointOfSales = allPointOfSales.filter(pos => {
    const matchesSearch = !searchTerm || 
      pos.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      pos.name.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'active' && pos.is_active) ||
      (statusFilter === 'inactive' && !pos.is_active);

    return matchesSearch && matchesStatus;
  });

  // Calcular estadísticas
  const totalPointOfSales = filteredPointOfSales.length;
  const activePointOfSales = filteredPointOfSales.filter(pos => pos.is_active).length;
  const inactivePointOfSales = filteredPointOfSales.filter(pos => !pos.is_active).length;

  const handleDeleteClick = (pointOfSale) => {
    setPointOfSaleToDelete(pointOfSale);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!pointOfSaleToDelete) return;
    
    try {
      const response = await deletePointOfSale(pointOfSaleToDelete.id);
      
      if (response.success) {
        setAllPointOfSales(prev => prev.filter(pos => pos.id !== pointOfSaleToDelete.id));
        setToast({
          isOpen: true,
          message: 'Punto de venta eliminado exitosamente',
          type: 'success'
        });
      } else {
        setToast({
          isOpen: true,
          message: response.message || 'Error al eliminar el punto de venta',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error al eliminar punto de venta:', error);
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al eliminar el punto de venta',
        type: 'error'
      });
    } finally {
      setIsDeleteModalOpen(false);
      setPointOfSaleToDelete(null);
    }
  };

  const handleFormSuccess = () => {
    // Recargar todos los puntos de venta desde el backend
    loadPointOfSales();
    setToast({
      isOpen: true,
      message: editingPointOfSale ? 'Punto de venta actualizado exitosamente' : 'Punto de venta creado exitosamente',
      type: 'success'
    });
  };

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">Puntos de Venta</h1>
              <p className="text-gray-600 mt-1">Gestione los puntos de venta y terminales del sistema</p>
            </div>
            <button
              onClick={() => {
                setEditingPointOfSale(null);
                setIsFormOpen(true);
              }}
              className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-md font-medium transition-colors"
            >
              + Nuevo Punto de Venta
            </button>
          </div>

          {/* Breadcrumb */}
          <nav className="text-sm text-gray-600 mb-4">
            <Link to="/dashboard" className="hover:text-teal-600">Dashboard</Link>
            <span className="mx-2">/</span>
            <Link to="/configuration" className="hover:text-teal-600">Configuración</Link>
            <span className="mx-2">/</span>
            <span className="text-gray-800">Puntos de Venta</span>
          </nav>

          {/* Filtros */}
          <div className="bg-white rounded-lg shadow-md p-4 mb-4">
            <div className="flex gap-2">
              {/* Filtro por Estado */}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="all">Todos los Estados</option>
                <option value="active">Activos</option>
                <option value="inactive">Inactivos</option>
              </select>

              {/* Búsqueda */}
              <input
                type="text"
                placeholder="Buscar por código o nombre..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
              <button
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('all');
                }}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors"
              >
                Limpiar Filtros
              </button>
            </div>
          </div>
        </div>

        {/* Estadísticas */}
        <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="text-sm text-gray-600">Total de Puntos de Venta</div>
            <div className="text-2xl font-bold text-gray-800">{totalPointOfSales}</div>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="text-sm text-gray-600">Puntos de Venta Activos</div>
            <div className="text-2xl font-bold text-green-600">{activePointOfSales}</div>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="text-sm text-gray-600">Puntos de Venta Inactivos</div>
            <div className="text-2xl font-bold text-red-600">{inactivePointOfSales}</div>
          </div>
        </div>

        {/* Tabla */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500">
              Cargando puntos de venta...
            </div>
          ) : filteredPointOfSales.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              {searchTerm || statusFilter !== 'all'
                ? 'No se encontraron puntos de venta con los filtros aplicados' 
                : 'No hay puntos de venta registrados'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Código
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Nombre
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ubicación
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredPointOfSales.map((pos) => (
                    <tr key={pos.id} className="hover:bg-gray-50">
                      {/* Código */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm font-mono font-medium text-teal-600">{pos.code}</span>
                      </td>

                      {/* Nombre */}
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{pos.name}</div>
                      </td>

                      {/* Ubicación */}
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-900">{pos.location || '-'}</span>
                      </td>

                      {/* Estado */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          pos.is_active 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {pos.is_active ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>

                      {/* Acciones */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => {
                            setEditingPointOfSale(pos);
                            setIsFormOpen(true);
                          }}
                          className="text-teal-600 hover:text-teal-900 mr-4"
                          title="Editar"
                        >
                          Editar
                        </button>
                        <button
                          onClick={() => handleDeleteClick(pos)}
                          className="text-red-600 hover:text-red-900"
                          title="Eliminar"
                        >
                          Eliminar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Modal de Formulario */}
        <PointOfSaleForm
          isOpen={isFormOpen}
          onClose={() => {
            setIsFormOpen(false);
            setEditingPointOfSale(null);
          }}
          pointOfSale={editingPointOfSale}
          onSuccess={handleFormSuccess}
        />

        {/* Modal de Confirmación de Eliminación */}
        <ConfirmModal
          isOpen={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false);
            setPointOfSaleToDelete(null);
          }}
          onConfirm={handleDeleteConfirm}
          title="Eliminar Punto de Venta"
          message={`¿Estás seguro de que deseas eliminar el punto de venta "${pointOfSaleToDelete?.name}" (${pointOfSaleToDelete?.code})? Esta acción no se puede deshacer.`}
          confirmText="Eliminar"
          cancelText="Cancelar"
          type="danger"
        />

        {/* Toast de notificaciones */}
        <Toast
          isOpen={toast.isOpen}
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, isOpen: false })}
        />
      </div>
    </div>
  );
};

export default PointOfSales;

